// Copyright (c) Microsoft. All rights reserved.

import { AuthConfig } from '../../auth/AuthHelper';
import { AlertType } from '../AlertType';
import { IChatUser } from '../users/ChatUser';
import { ServiceInfo } from '../ServiceInfo';

// This is the default user information when authentication is set to 'None'.
// It must match what is defined in PassthroughAuthenticationHandler.cs on the backend.
export const DefaultChatUser: IChatUser = {
  id: 'c05c61eb-65e4-4223-915a-fe72b0c9ece1',
  emailAddress: 'user@contoso.com',
  fullName: 'Default User',
  online: true,
  isTyping: false,
};

export const DefaultActiveUserInfo: ActiveUserInfo = {
  id: DefaultChatUser.id,
  email: DefaultChatUser.emailAddress,
  username: DefaultChatUser.fullName,
};

export enum AppConnectState {
  ProbeForBackend,
  SettingUserInfo,
  ErrorLoadingChats,
  ErrorLoadingUserInfo,
  LoadingChats,
  Chat,
  SigningOut,
}

export interface ActiveUserInfo {
  id: string;
  email: string;
  username: string;
}

export interface Alert {
  message: string;
  type: AlertType;
  id?: string;
  onRetry?: () => void;
}

export interface AlertModalMessage {
  altHeader: string;
  altMessage: string;
}

export interface AlertToast {
  altMessage: string;
  altType?: AlertType;
}

interface Feature {
  enabled: boolean; // Whether to show the feature in the UX
  label: string;
  inactive?: boolean; // Set to true if you don't want the user to control the visibility of this feature or there's no backend support
  description?: string;
}

export interface Setting {
  title: string;
  description?: string;
  features: FeatureKeys[];
  stackVertically?: boolean;
  learnMoreLink?: string;
}

export interface AppState {
  alerts: Alert[];

  // Modal Alert
  altModalVisible: boolean;
  altModalMessage: AlertModalMessage;

  // Toast Alert
  toastVisible: boolean;
  toastMessage: AlertToast[];

  activeUserInfo?: ActiveUserInfo;
  authConfig?: AuthConfig | null;
  features: Record<FeatureKeys, Feature>;
  settings: Setting[];
  serviceInfo: ServiceInfo;

  isMaintenance: boolean;
  appConnectState: AppConnectState; // 추가된 부분
  viewType: string;

  settingDialogVisible: boolean;
  settingDialogType: number;
}

export enum FeatureKeys {
  DarkMode,
  SimplifiedExperience,
  PluginsPlannersAndPersonas,
  AzureContentSafety,
  AzureAISearch,
  BotAsDocs,
  MultiUserChat,
  RLHF, // Reinforcement Learning from Human Feedback
}

export const Features = {
  [FeatureKeys.DarkMode]: {
    enabled: false,
    label: 'Dark Mode',
  },
  [FeatureKeys.SimplifiedExperience]: {
    enabled: true,
    label: 'Simplified Chat Experience',
  },
  [FeatureKeys.PluginsPlannersAndPersonas]: {
    enabled: true,
    label: 'Plugins & Planners & Personas',
    description: 'The Plans and Persona tabs are hidden until you turn this on',
  },
  [FeatureKeys.AzureContentSafety]: {
    enabled: false,
    label: 'Azure Content Safety',
    inactive: true,
  },
  [FeatureKeys.AzureAISearch]: {
    enabled: false,
    label: 'Azure AI Search',
    inactive: true,
  },
  [FeatureKeys.BotAsDocs]: {
    enabled: false,
    label: 'Export Chat Sessions',
  },
  [FeatureKeys.MultiUserChat]: {
    enabled: false,
    label: 'Live Chat Session Sharing',
    description: 'Enable multi-user chat sessions. Not available when authorization is disabled.',
  },
  [FeatureKeys.RLHF]: {
    enabled: false,
    label: 'Reinforcement Learning from Human Feedback',
    description: 'Enable users to vote on model-generated responses. For demonstration purposes only.',
    // TODO: [Issue #42] Send and store feedback in backend
  },
};

export const Settings = [
  {
    // Basic settings has to stay at the first index. Add all new settings to end of array.
    title: 'Basic',
    features: [FeatureKeys.DarkMode, FeatureKeys.PluginsPlannersAndPersonas],
    stackVertically: true,
  },
  {
    title: 'Display',
    features: [FeatureKeys.SimplifiedExperience],
    stackVertically: true,
  },
  {
    title: 'Azure AI',
    features: [FeatureKeys.AzureContentSafety, FeatureKeys.AzureAISearch],
    stackVertically: true,
  },
  {
    title: 'Experimental',
    description: 'The related icons and menu options are hidden until you turn this on',
    features: [FeatureKeys.BotAsDocs, FeatureKeys.MultiUserChat, FeatureKeys.RLHF],
  },
];

export const initialState: AppState = {
  alerts: [],

  // Modal Alert
  altModalVisible: false,
  altModalMessage: {
    altHeader: '알림',
    altMessage: '내용을 입력해주세요',
  },

  // Toast Alert
  toastVisible: false,
  toastMessage: [],
  activeUserInfo: DefaultActiveUserInfo,
  authConfig: {} as AuthConfig,
  features: Features,
  settings: Settings,
  serviceInfo: {
    memoryStore: { types: [], selectedType: '' },
    availablePlugins: [],
    version: '',
    isContentSafetyEnabled: false,
  },
  isMaintenance: false,
  appConnectState: AppConnectState.SettingUserInfo,
  viewType: 'Desktop',

  settingDialogVisible: false,
  settingDialogType: 1,
};
